// Utility functions to compute business metrics for stands
// Keeping types flexible to avoid import cycles; uses duck typing.

export interface MaintenanceLike { date?: string | number | Date; type?: string; status?: string }
export interface PublicationStockEntry { publicationId: string; quantity: number }
export interface PublicationDef { id: string; minStock?: number; title?: string }
export interface StandLike {
  id: string;
  isReserved?: boolean;
  reservedUntil?: string | number | Date | null;
  maintenanceHistory?: MaintenanceLike[];
  publications?: PublicationStockEntry[];
  createdAt?: string | number | Date;
}

const toDate = (d: any): Date | undefined => {
  if (!d) return undefined;
  if (d instanceof Date) return isNaN(d.getTime()) ? undefined : d;
  const n = typeof d === 'number' ? new Date(d) : new Date(String(d));
  return isNaN(n.getTime()) ? undefined : n;
};

export const getLastMaintenance = (stand: StandLike) => {
  const hist = stand.maintenanceHistory || [];
  if (!hist.length) return undefined;
  return hist[hist.length - 1];
};

export const getNextPreventiveDue = (stand: StandLike, intervalMonths: number) => {
  const last = getLastMaintenance(stand);
  const lastDate = last ? toDate(last.date) : undefined;
  if (!lastDate) return undefined;
  const due = new Date(lastDate);
  due.setMonth(due.getMonth() + intervalMonths);
  return due;
};

export const hasCurativePending = (stand: StandLike) => {
  return (stand.maintenanceHistory || []).some(m => m.type === 'curative' && m.status === 'pending');
};

export const needsMaintenance = (stand: StandLike, intervalMonths: number, ref = new Date()) => {
  if (hasCurativePending(stand)) {
    return { needed: true, reason: 'curative' } as const;
  }
  const next = getNextPreventiveDue(stand, intervalMonths);
  if (!next) return { needed: true, reason: 'no-history' } as const;
  if (ref > next) return { needed: true, reason: 'preventive-due' } as const;
  return { needed: false, reason: 'ok', nextDue: next } as const;
};

export const lowStockPublications = (stand: StandLike, publications: PublicationDef[]) => {
  const entries = stand.publications || [];
  return entries.filter(e => {
    const pub = publications.find(p => p.id === e.publicationId);
    if (!pub || pub.minStock == null) return false;
    return e.quantity < pub.minStock;
  });
};

export const computeStandMetrics = (stand: StandLike, publications: PublicationDef[], intervalMonths: number) => {
  const maintenance = needsMaintenance(stand, intervalMonths);
  const lowStock = lowStockPublications(stand, publications);
  const created = toDate(stand.createdAt);
  const ageDays = created ? Math.floor((Date.now() - created.getTime()) / 86400000) : undefined;
  return {
    maintenance,
    lowStock,
    lowStockCount: lowStock.length,
    ageDays,
    isReserved: !!stand.isReserved,
    reservedUntil: toDate(stand.reservedUntil)
  };
};
